<template>
  <div>
    <div class="d-flex justify-space-between">
      <div class="d-flex">
        <v-btn icon @click="$emit('back')">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <h2 class="page-title-bar">{{ $t("Shelf Monitoring Detail") }}</h2>
      </div>
      <div>
        <v-btn class="btn mr-2" text @click="captureScheduleDialog = true">{{
          $t("Capture Schedule")
        }}</v-btn>
        <v-btn class="btn" text @click="captureNow">
          <v-icon left>mdi-camera</v-icon>{{ $t("Capture Now") }}
        </v-btn>
      </div>
    </div>

    <div class="mt-4 detail-info-row">
      <div class="info-col">
        <div class="info-label">{{ $t("Location") }}</div>
        <div class="info-value">{{ location }}</div>
      </div>
      <div class="info-divider"></div>
      <div class="info-col">
        <div class="info-label">{{ $t("Linked Shelf") }}</div>
        <div class="info-value">{{ linkedShelves }}</div>
      </div>
      <div class="info-divider"></div>
      <div class="info-col">
        <div class="info-label">{{ $t("Linked Camera") }}</div>
        <div class="info-value">{{ linkedCameras }}</div>
      </div>
    </div>

    <div class="detail-tabs-row">
      <button
        v-for="tab in tabs"
        :key="tab"
        class="detail-tab-btn"
        :class="{ active: selectedTab === tab }"
        @click="handleTab(tab)"
      >
        {{ tab }}
      </button>
    </div>

    <!-- ── ALL TAB ─────────────────────────────────────────────────────────── -->
    <template v-if="selectedTab === 'All'">
      <div class="detail-main">
        <div class="detail-capture-list">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>{{ $t("Capture Time") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="capture in captureList"
                :key="capture.id"
                class="capture-row"
                :class="{
                  'capture-row--selected': selectedCapture === capture.id
                }"
                @click="handleSelectCapture(capture)"
              >
                <td>{{ capture.id }}</td>
                <td>{{ capture.captureTime }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="detail-shelf-images">
          <div class="detail-shelf-images-title">{{ $t("Shelf Status") }}</div>
          <div class="detail-images-area">
            <div class="block-image">
              <div ref="allShelfContainer" class="vrog-container"></div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- ── INDIVIDUAL SHELF TAB ───────────────────────────────────────────── -->
    <template v-else>
      <div class="detail-main-multi-block">
        <!-- Capture list -->
        <div class="detail-capture-list-block">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>{{ $t("Capture Time") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="capture in shelfcaptureList"
                :key="capture.id"
                class="capture-row"
                :class="{
                  'capture-row--selected': selectedCaptureIndex === capture.id
                }"
                @click="handleSelectShelfCapture(capture)"
              >
                <td>{{ capture.id }}</td>
                <td>{{ capture.created }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Error table -->
        <div class="detail-table-block">
          <table class="detail-error-table">
            <thead>
              <tr>
                <th>{{ $t("Shelf Error") }}</th>
                <th>{{ $t("Product Name") }}</th>
                <th>{{ $t("Action") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!errorProducts.length">
                <td colspan="3" class="text-center">{{ $t("No issues found") }}</td>
              </tr>
              <tr
                v-else
                v-for="(item, index) in errorProducts"
                :key="index"
                class="error-table-row"
                :class="{ 'error-row--selected': selectedErrorIndex === index }"
                @click="handleErrorRowClick(item, index)"
              >
                <td class="error-cell">{{ item.status || '-' }}</td>
                <td>{{ item.articleName || '-' }}</td>
                <td>Auto</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- ROG panel — VROGRenderer mounts here -->
        <div class="detail-images-block">
          <div class="detail-block-title">
            <span>{{ $t("ROG") }}</span>
            <v-icon small @click="expandRog">mdi-fullscreen</v-icon>
          </div>
          <div class="detail-block-content">
            <template v-if="imageLoading">
              <div class="no-image">
                <span>{{ $t('Loading image') }}...</span>
              </div>
            </template>
            <template v-else-if="selectedIndividualShelf && selectedIndividualShelf.rogImage">
              <div class="block-image">
                <div ref="rogContainer" class="vrog-container"></div>
              </div>
            </template>
            <div v-else class="no-image">
              {{ $t("No Image Available") }}
            </div>
          </div>
        </div>

        <!-- Shelf Status panel — VROGRenderer mounts here -->
        <div class="detail-images-block">
          <div class="detail-block-title">
            <span>{{ $t("Shelf Status") }}</span>
            <v-icon small @click="expandShelfStatus">mdi-fullscreen</v-icon>
          </div>
          <div class="detail-block-content">
            <template v-if="imageLoading">
              <div class="no-image">
                <span>{{ $t('Loading image') }}...</span>
              </div>
            </template>
            <template v-else-if="selectedIndividualShelf && selectedIndividualShelf.shelfImage">
              <div class="block-image">
                <div ref="shelfContainer" class="vrog-container"></div>
              </div>
            </template>
            <div v-else class="no-image">
              {{ $t("No Image Available") }}
            </div>
          </div>
        </div>
      </div>
    </template>

    <v-dialog v-model="displaymodel" v-if="showRog || showShelfStatus" persistent :retain-focus="false" max-width="700"
      transition="dialog-bottom-transition">
      <v-card class="d-flex flex-column zoom-dialog-card">
        <div
          style="height: 64px; background-color: #001E38; width: 100%; display: flex; align-items: center; padding: 0 16px;">
          <v-btn icon dark small :disabled="shelfStatusZoom <= 0.25" @click="zoomOut" :title="$t('Zoom Out')">
            <v-icon>mdi-magnify-minus-outline</v-icon>
          </v-btn>
          <span class="zoom-label">{{ Math.round(shelfStatusZoom * 100) }}%</span>
          <v-btn icon dark small :disabled="shelfStatusZoom >= 5" @click="zoomIn" :title="$t('Zoom In')">
            <v-icon>mdi-magnify-plus-outline</v-icon>
          </v-btn>
          <v-btn icon dark small @click="resetZoom" :title="$t('Reset Zoom')">
            <v-icon>mdi-magnify-close</v-icon>
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="handleClose">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <div ref="imageViewport" class="image-viewport" @wheel.prevent="onWheel" @mousedown.prevent="onMouseDown"
          @mousemove="onMouseMove" @mouseup="onMouseUp" @mouseleave="onMouseUp"
          :style="{ cursor: isDragging ? 'grabbing' : 'grab' }">
          <div v-if="showRog" class="image-container">
            <div ref="rogModalContainer" class="vrog-container" :style="{
              transform: `translate(${translateX}px, ${translateY}px) scale(${shelfStatusZoom})`,
              transformOrigin: '0 0'
            }" @load="onImageLoad" @dragstart.prevent></div>
          </div>
          <div v-else-if="showShelfStatus" class="image-container">
            <div ref="shelfModalContainer" class="vrog-container" :style="{
              transform: `translate(${translateX}px, ${translateY}px) scale(${shelfStatusZoom})`,
              transformOrigin: '0 0'
            }" @load="onImageLoad" @dragstart.prevent></div>
          </div>
          <div v-else class="no-image-placeholder">{{ $t('No Image Available') }}</div>
        </div>
      </v-card>
    </v-dialog>

    <!-- ── Capture Schedule Dialog ────────────────────────────────────────── -->
    <v-dialog v-model="captureScheduleDialog" persistent max-width="760" class="schedule-dialog">
      <v-card class="popup add_store_popup">
          <h3 class="page-title-bar mb-8"><v-icon size="28" class="mr-3 mt-1" color="#001e38">mdi-clock-outline</v-icon>{{ $t("Capture Schedule") }}</h3>
          <div class="capture-table-card mb-5">
            <table class="capture-table">
              <thead>
                <tr>
                  <th class="index-col">#</th>
                  <th>{{ $t("ROG Capture Time") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!rogCaptureTimes.length">
                  <td colspan="2" class="text-center">{{ $t("No data available") }}</td>
                </tr>
                <tr v-for="(item, index) in rogCaptureTimes" :key="index">
                  <td>{{ index + 1 }}</td>
                  <td>{{ item }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="capture-table-card">
            <table class="capture-table">
              <thead>
                <tr>
                  <th class="index-col">#</th>
                  <th>{{ $t("Shelf Status Capture Time") }}</th>
                </tr>
              </thead>
            </table>
            <div class="capture-table-scroll">
              <table class="capture-table">
                <tbody>
                  <tr v-if="!shelfCaptureTimes.length">
                    <td colspan="2" class="text-center">{{ $t("No data available") }}</td>
                  </tr>
                  <tr v-for="(item, index) in shelfCaptureTimes" :key="index">
                    <td class="index-col">{{ index + 1 }}</td>
                    <td>{{ item }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        <div class="capture-modal-footer">
          <v-btn class="btn" text @click="captureScheduleDialog = false">
            {{ $t("Close") }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import codes from '@/plugins/codes'
import VROGRenderer from '@/lib/vrog-renderer.js'
import { mapGetters } from 'vuex'
import EventBus from '@/plugins/eventBus'
import commons from '@/plugins/commons'

export default {
  props: {
    shelfData: {
      type: Object,
      default: () => ({})
    }
  },

  data () {
    return {
      shelfStatusZoom: 1,
      translateX: 0,
      translateY: 0,
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      displaymodel: false,
      location: `${this.shelfData.floor} / ${this.shelfData.section} / ${this.shelfData.shelfGroupId}`,
      selectedTab: 'All',
      selectedCapture: 1,
      selectedCaptureIndex: null,
      selectedErrorIndex: null,
      shelfcaptureList: [],
      showRog: false,
      showShelfStatus: false,
      rogZoom: 1,
      isCtrlHeld: false,
      tabs: ['All'],

      // Individual shelf tab
      allShelfInfo: [],
      rogShelfInfo: [],
      shelfInfo: [],
      selectedIndividualShelf: [],
      captureList: [],
      errorProducts: [],

      captureScheduleDialog: false,
      rogCaptureTimes: [],
      shelfCaptureTimes: [],
      shelfGroupDetails: null,
      shelfList: null,
      selectedShelfId: null,
      imageLoading: false,
      color: 'rgb(177, 156, 217, 0.1)'
    }
  },

  computed: {
    ...mapGetters({
      store: 'dataStore/GET_SELECTED_STORE'
    }),
    linkedShelves () {
      if (!this.shelfGroupDetails) return ''
      return this.shelfGroupDetails.groupList[0].shelfList.reduce(
        (acc, shelf) => (acc ? `${acc}, ${shelf}` : shelf),
        ''
      )
    },
    linkedCameras () {
      if (!this.shelfGroupDetails) return ''
      return this.shelfGroupDetails.groupList[0].labelInfo.reduce(
        (acc, cam) => (acc ? `${acc}, ${cam.labelCode}` : cam.labelCode),
        ''
      )
    }
  },

  methods: {
    handleClose () {
      this.showRog = false
      this.showShelfStatus = false
      this.displaymodel = false
      this.resetZoom()
    },
    centerImage (imgEl) {
      const viewport = this.$refs.imageViewport
      if (!viewport || !imgEl) return
      const vw = viewport.clientWidth
      const vh = viewport.clientHeight
      const iw = imgEl.naturalWidth
      const ih = imgEl.naturalHeight
      if (!iw || !ih) return
      const fitZoom = parseFloat(Math.min(vw / iw, vh / ih).toFixed(4))
      this.shelfStatusZoom = fitZoom
      this.translateX = (vw - iw * fitZoom) / 2
      this.translateY = (vh - ih * fitZoom) / 2
    },
    onImageLoad (e) {
      this.$nextTick(() => this.centerImage(e.target))
    },
    resetZoom () {
      const viewport = this.$refs.imageViewport
      const img = viewport ? viewport.querySelector('img') : null
      if (img && img.complete && img.naturalWidth) {
        this.centerImage(img)
      } else {
        this.shelfStatusZoom = 1
        this.translateX = 0
        this.translateY = 0
      }
    },
    zoomIn () {
      const newZoom = Math.min(5, parseFloat((this.shelfStatusZoom + 0.25).toFixed(2)))
      const viewport = this.$refs.imageViewport
      if (viewport) {
        const rect = viewport.getBoundingClientRect()
        const cx = rect.width / 2
        const cy = rect.height / 2
        const factor = newZoom / this.shelfStatusZoom
        this.translateX = cx - factor * (cx - this.translateX)
        this.translateY = cy - factor * (cy - this.translateY)
      }
      this.shelfStatusZoom = newZoom
    },
    zoomOut () {
      const newZoom = Math.max(0.25, parseFloat((this.shelfStatusZoom - 0.25).toFixed(2)))
      const viewport = this.$refs.imageViewport
      if (viewport) {
        const rect = viewport.getBoundingClientRect()
        const cx = rect.width / 2
        const cy = rect.height / 2
        const factor = newZoom / this.shelfStatusZoom
        this.translateX = cx - factor * (cx - this.translateX)
        this.translateY = cy - factor * (cy - this.translateY)
      }
      this.shelfStatusZoom = newZoom
    },
    onWheel (e) {
      const step = 0.1
      const direction = e.deltaY < 0 ? 1 : -1
      const newZoom = Math.max(0.25, Math.min(5, parseFloat((this.shelfStatusZoom + direction * step).toFixed(2))))
      if (newZoom === this.shelfStatusZoom) return
      const viewport = this.$refs.imageViewport
      const rect = viewport.getBoundingClientRect()
      const mouseX = e.clientX - rect.left
      const mouseY = e.clientY - rect.top
      const factor = newZoom / this.shelfStatusZoom
      this.translateX = mouseX - factor * (mouseX - this.translateX)
      this.translateY = mouseY - factor * (mouseY - this.translateY)
      this.shelfStatusZoom = newZoom
    },
    onMouseDown (e) {
      this.isDragging = true
      this.dragStartX = e.clientX - this.translateX
      this.dragStartY = e.clientY - this.translateY
    },
    onMouseMove (e) {
      if (!this.isDragging) return
      this.translateX = e.clientX - this.dragStartX
      this.translateY = e.clientY - this.dragStartY
    },
    onMouseUp () {
      this.isDragging = false
    },
    onRogClick (e) {
      if (e.ctrlKey) {
        this.rogZoom = Math.max(0.25, +(this.rogZoom - 0.25).toFixed(2))
      } else {
        this.rogZoom = Math.min(5, +(this.rogZoom + 0.25).toFixed(2))
      }
    },
    onShelfStatusClick (e) {
      if (e.ctrlKey) {
        this.shelfStatusZoom = Math.max(0.25, +(this.shelfStatusZoom - 0.25).toFixed(2))
      } else {
        this.shelfStatusZoom = Math.min(5, +(this.shelfStatusZoom + 0.25).toFixed(2))
      }
    },
    expandRog () {
      this.showRog = true
      this.displaymodel = true
      this.rogZoom = 1
      this.$nextTick(() => {
        this._initRenderersForModal(
          this.selectedIndividualShelf.rogImage,
          this.selectedIndividualShelf.shelfImage
        )
      })
    },
    expandShelfStatus () {
      this.showShelfStatus = true
      this.displaymodel = true
      this.shelfStatusZoom = 1
      this.$nextTick(() => {
        this._initRenderersForModal(
          this.selectedIndividualShelf.rogImage,
          this.selectedIndividualShelf.shelfImage
        )
      })
    },
    captureNow () {
      const body = this.linkedCameras.split(', ').map(label => label)

      const params = {
        store: this.store.code
      }
      const config = { params }
      this.$utils
        .callAxiosWithBody(
          codes.requests.toPutCaptureNow.method,
          codes.requests.toPutCaptureNow.url,
          body,
          config
        )
        .then(res => {
          if (res.status === 200) {
            EventBus.$emit('messageAlert', this.$t(res.data.returnMsg))
          }
        })
        .catch(error => {
          console.log(error)
          EventBus.$emit(
            'messageAlert',
            this.$t(error.response.data.returnMsg),
            'error'
          )
        })
    },
    // ── Tab / capture handlers ───────────────────────────────────────────────

    handleSelectCapture (capture) {
      this.selectedCapture = capture.id
      this.allShelfInfo = capture.shelfInfo.flatMap(data => data)
      this.$nextTick(() => {
        this._initRenderersStitch(capture.stitchImg)
      })
    },

    handleTab (tab) {
      this.selectedTab = tab
      // Reset highlight state when switching tabs
      this.selectedErrorIndex = null
      if (tab !== 'All') {
        this.selectedShelfId = tab
        this.shelfcaptureList = []
        this.errorProducts = []
        this.rogShelfInfo = []
        this.shelfInfo = []
        this.selectedIndividualShelf = {}
        this.selectedCaptureIndex = null
        this.getShelfHistory()
      } else {
        this.selectedShelfId = null
        this.allShelfInfo = this.captureList[0].shelfInfo.flatMap(data => data)
        this.$nextTick(() => {
          this._initRenderersStitch(this.captureList[0].stitchImg)
        })
      }
    },

    // ── Error row click — highlight matching cluster in both renderers ────────

    handleSelectShelfCapture (item) {
      this.selectedCaptureIndex = item.id
      this.selectedIndividualShelf = item
    },
    handleErrorRowClick (item, index) {
      // Toggle off when clicking the same row
      if (this.selectedErrorIndex === index) {
        this.selectedErrorIndex = null
        if (this._rogRenderer) this._rogRenderer.clearHighlight()
        if (this._shelfRenderer) this._shelfRenderer.clearHighlight()
        return
      }

      this.selectedErrorIndex = index

      // VROGRenderer keys clusters by esl_info[0].label_code
      // shelfInfo stores this as eslInfo[0].labelCode (camelCase)
      const labelCode = item.eslInfo?.[0]?.labelCode || ''

      if (this._rogRenderer) this._rogRenderer.highlightByKey(labelCode)
      if (this._shelfRenderer) this._shelfRenderer.highlightByKey(labelCode)
    },

    // ── VROGRenderer init ────────────────────────────────────────────────────
    async _initRenderersForModal (rogImageUrl, shelfImageUrl) {
      // Destroy previous instances before re-rendering
      if (this._rogModalRenderer) {
        this._rogModalRenderer.destroy()
        this._rogModalRenderer = null
      }
      if (this._shelfModalRenderer) {
        this._shelfModalRenderer.destroy()
        this._shelfModalRenderer = null
      }

      // ROG: rogShelfInfo already uses snake_case (cluster_bbox, esl_info, prd_info)
      // which matches exactly what VROGRenderer expects — no transformation needed
      if (this.$refs.rogModalContainer) {
        this._rogModalRenderer = new VROGRenderer(
          this.$refs.rogModalContainer,
          rogImageUrl,
          this.rogShelfInfo,
          this.color
        )
        await this._rogModalRenderer.render()
      }

      // Shelf Status: shelfInfo uses camelCase so we normalise to snake_case
      // before passing to VROGRenderer
      if (this.$refs.shelfModalContainer) {
        this._shelfModalRenderer = new VROGRenderer(
          this.$refs.shelfModalContainer,
          shelfImageUrl,
          this._normaliseShelfInfo(this.shelfInfo),
          this.color
        )
        await this._shelfModalRenderer.render()
      }
    },
    async _initRenderers (rogImageUrl, shelfImageUrl) {
      // Destroy previous instances before re-rendering
      if (this._rogRenderer) {
        this._rogRenderer.destroy()
        this._rogRenderer = null
      }
      if (this._shelfRenderer) {
        this._shelfRenderer.destroy()
        this._shelfRenderer = null
      }

      // ROG: rogShelfInfo already uses snake_case (cluster_bbox, esl_info, prd_info)
      // which matches exactly what VROGRenderer expects — no transformation needed
      this._rogRenderer = new VROGRenderer(
        this.$refs.rogContainer,
        rogImageUrl,
        this.rogShelfInfo,
        this.color
      )
      await this._rogRenderer.render()

      // Shelf Status: shelfInfo uses camelCase so we normalise to snake_case
      // before passing to VROGRenderer
      this._shelfRenderer = new VROGRenderer(
        this.$refs.shelfContainer,
        shelfImageUrl,
        this._normaliseShelfInfo(this.shelfInfo),
        this.color
      )
      await this._shelfRenderer.render()
    },
    async _initRenderersStitch (shelfImageUrl) {
      if (this._stichRenderer) {
        this._stichRenderer.destroy()
        this._stichRenderer = null
      }
      this._stichRenderer = new VROGRenderer(
        this.$refs.allShelfContainer,
        shelfImageUrl,
        this.allShelfInfo,
        this.color
      )

      await this._stichRenderer.render()
    },

    // Converts shelfInfo camelCase keys → snake_case for VROGRenderer
    _normaliseShelfInfo (shelfInfo) {
      return shelfInfo.map(row =>
        row.map(cell => ({
          // Required by VROGRenderer._flattenShelfInfo → highlightByKey
          cluster_bbox: cell.clusterBBox,
          esl_info: (cell.eslInfo || []).map(e => ({
            label_code: e.labelCode,
            esl_bbox: e.eslBBox
          })),
          prd_info: [
            {
              article_name: cell.articleName,
              article_id: cell.articleId
            }
          ],
          // Pass through status so future extensions can use it
          status: cell.status
        }))
      )
    },

    // ── API calls ────────────────────────────────────────────────────────────

    getShelveInfo () {
      return new Promise(resolve => {
        const params = {
          company: this.$store.state.auth.user.company,
          store: this.store.code,
          floor: this.shelfData.floor,
          shelfgroup: this.shelfData.shelfGroupId
        }
        this.$utils
          .callAxios(
            codes.requests.getShelves.method,
            codes.requests.getShelves.url,
            { params }
          )
          .then(res => {
            if (res?.data?.shelfGroupList && res.data.shelfGroupList.length) {
              this.shelfGroupDetails = res.data.shelfGroupList[0]
              this.shelfList = this.shelfGroupDetails.groupList[0].shelfList
              const stitchList = this.shelfGroupDetails.groupList[0].stitchList
              this.captureList = stitchList.map((data, index) => ({
                id: index + 1,
                ...data,
                captureTime: commons.convertDate(data.captureTime)
              }))
              if (this.captureList.length) {
                this.handleSelectCapture(this.captureList[0])
              }
              this.tabs = ['All', ...this.shelfList]
              resolve()
            }
          })
      })
    },

    getShelfHistory () {
      this.imageLoading = true
      this.clearShelfHistory()
      const params = {
        company: this.$store.state.auth.user.company,
        store: this.store.code,
        floor: this.shelfData.floor,
        inputBatchId: '',
        shelfId: this.selectedShelfId,
        page: 0,
        size: 500
      }
      this.$utils
        .callAxios(
          codes.requests.getShelfHistory.method,
          codes.requests.getShelfHistory.url,
          { params }
        )
        .then(res => {
          this.imageLoading = false
          if (
            !res.data ||
            !res.data.operationHistoryInfoList ||
            !res.data.operationHistoryInfoList.length
          ) {
            return
          }
          if (
            res?.data?.operationHistoryInfoList &&
            res.data.operationHistoryInfoList.length
          ) {
            this.shelfcaptureList = res.data.operationHistoryInfoList.map(
              (item, index) => {
                return {
                  id: index + 1,
                  ...item,
                  created: commons.convertDate(item.created) || '-'
                }
              }
            )
            this.handleSelectShelfCapture(this.shelfcaptureList[0])

            const history = res.data.operationHistoryInfoList[0]
            this.rogShelfInfo = history.rogShelfInfo || []
            this.shelfInfo = history.shelfInfo || []

            // Build error product list from shelfInfo
            this.errorProducts = []
            this.selectedErrorIndex = null
            this.shelfInfo.forEach(row => {
              row.forEach(col => {
                if (col.status !== 'normal') this.errorProducts.push(col)
              })
            })

            // Wait for the tab's DOM refs to be mounted, then render
            this.$nextTick(() => {
              this._initRenderers(history.rogImage, history.shelfImage)
            })
          }
        }).catch(() => {
          this.imageLoading = false
          this.clearShelfHistory()
        })
    },
    clearShelfHistory () {
      this.shelfcaptureList = []
      this.errorProducts = []
      this.rogShelfInfo = []
      this.shelfInfo = []
      this.selectedIndividualShelf = {}
      this.selectedCaptureIndex = null

      if (this._rogRenderer) {
        this._rogRenderer.destroy()
        this._rogRenderer = null
      }

      if (this._shelfRenderer) {
        this._shelfRenderer.destroy()
        this._shelfRenderer = null
      }
    },
    _getBaseScheduleParams () {
      return {
        company: this.$store.state.auth.user.company,
        store: this.store.code,
        label: this.linkedCameras.split(', ')[0]
      }
    },
    getSchedule () {
      const params = this._getBaseScheduleParams()
      this.$utils
        .callAxios(
          codes.requests.getVisionScheule.method,
          codes.requests.getVisionScheule.url,
          { params }
        )
        .then(res => {
          if (res?.data.scheduleList) {
            this.rogCaptureTimes = res?.data.scheduleList.map(data => {
              return commons.convertDate(data.startTime)
            })
          }
        })
    },
    getRepeatSchedule () {
      const params = this._getBaseScheduleParams()
      this.$utils
        .callAxios(
          codes.requests.getVisionRepeatSchedule.method,
          codes.requests.getVisionRepeatSchedule.url,
          { params }
        )
        .then(res => {
          if (res?.data.repeatScheduleList) {
            this.shelfCaptureTimes = res?.data.repeatScheduleList.map(data => {
              const parsedTime = data.applyTime.split('~')[0].trim()

              // 2. Output the final 12-hour formatted string
              const finalDisplay = commons.convertDate(parsedTime)
              return finalDisplay
            })
          }
        })
    }
  },

  beforeDestroy () {
    if (this._rogRenderer) this._rogRenderer.destroy()
    if (this._shelfRenderer) this._shelfRenderer.destroy()
    if (this._stichRenderer) this._stichRenderer.destroy()
    window.removeEventListener('keydown', this._onKeyDown)
    window.removeEventListener('keyup', this._onKeyUp)
  },

  async mounted () {
    this._onKeyDown = (e) => { if (e.key === 'Control') this.isCtrlHeld = true }
    this._onKeyUp = (e) => { if (e.key === 'Control') this.isCtrlHeld = false }
    window.addEventListener('keydown', this._onKeyDown)
    window.addEventListener('keyup', this._onKeyUp)
    document.documentElement.scrollTop = 0
    await this.getShelveInfo()
    this.getSchedule()
    this.getRepeatSchedule()
    console.log('shelfData', this.shelfData)
  },
  created () {
    // VROGRenderer instances (not reactive — plain refs)
    this._rogRenderer = null
    this._shelfRenderer = null
    this._rogModalRenderer = null
    this._shelfModalRenderer = null
    this._stichRenderer = null
  }
}
</script>

<style scoped>
.image-container {
  height: calc(90vh - 48px);
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: auto;
  position: relative;
}

.reset {
  position: absolute;
  right: 18px;
  bottom: 18px;
}

.reset-btn {
  background: #001e38 !important;
  color: #fff !important;
  min-width: 48px !important;
  min-height: 48px !important;
}

.detail-info-row {
  display: grid;
  grid-template-columns: 0.6fr 1.1fr 1fr;
  min-height: 70px;
  background: #fafafa;
  border: 1px solid #ebebeb;
  border-radius: 4px;
  padding: 12px 24px;
  margin-bottom: 20px;
}

.info-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.info-label {
  font-size: 12px;
  color: #717680;
  font-weight: 600;
  line-height: 16px;
}

.info-value {
  font-size: 14px;
  color: #484848;
  font-weight: 400;
  line-height: 20px;
  word-break: break-word;
}

.info-divider {
  display: none;
}

/* ── Tabs ── */
.detail-tabs-row {
  display: flex;
  margin: 20px 0;
  flex-wrap: wrap;
}

.detail-tab-btn {
  padding: 6px 18px;
  border: 1px solid #d0d0d0;
  background: #fff;
  color: #555;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  border-right: none;
}
.detail-tab-btn:first-child {
  border-radius: 4px 0 0 4px;
}
.detail-tab-btn:last-child {
  border-radius: 0 4px 4px 0;
  border-right: 1px solid #d0d0d0;
}
.detail-tab-btn:hover {
  background: #f5f5f5;
}
.detail-tab-btn.active {
  background: #001e38;
  color: #fff;
  border-color: #001e38;
}

/* ── All-tab layout ── */
.detail-main {
  display: flex;
  gap: 20px;
}

/* ── Individual-shelf layout ── */
.detail-main-multi-block {
  display: flex;
  flex: 1;
  gap: 20px;
  overflow: auto;
  min-height: 0;
}

/* ── Capture list ── */
.detail-capture-list,
.detail-capture-list-block {
  width: 200px;
  min-width: 200px;
  height: 576px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow-y: auto;
  background: #fff;
}

.detail-capture-list table,
.detail-capture-list-block table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.detail-capture-list thead th,
.detail-capture-list-block thead th {
  position: sticky;
  top: 0;
  background: #ececec;
  color: #4d4f5c;
  text-align: left;
  padding: 8px 10px;
  font-weight: 700;
  font-size: 14px;
  z-index: 1;
}

.capture-row {
  cursor: pointer;
}
.capture-row td {
  padding: 8px 10px;
  border-bottom: 1px solid #f3f3f5;
}
.capture-row--selected {
  background: #001e38;
  color: white;
}

/* ── Image panels ── */
.detail-shelf-images,
.detail-images-block {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 576px;
  background: #f9f9f9;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  min-width: 0;
}
.detail-images-block {
  min-width: 300px !important;
}

.detail-shelf-images-title,
.detail-block-title {
  font-size: 14px;
  font-weight: 700;
  color: #4d4f5c;
  padding: 8px 10px;
  border-bottom: 1px solid #f3f3f5;
  background: #ececec;
  flex-shrink: 0;
}
.detail-block-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.detail-images-area,
.detail-block-content {
  flex: 1;
  overflow: auto;
  padding: 8px;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  min-height: 0;
}

.block-image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: flex-start;
}

/* VROGRenderer mount point — scrollable when canvas is wider than panel */
.vrog-container {
  width: 100%;
  height: 100%;
  overflow: auto;
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.status-image {
  height: 100%;
  width: auto;
  min-width: fit-content;
}

/* ── Error table ── */
.detail-table-block {
  flex: 1;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: auto;
  background: #fff;
  min-width: 300px;
}

.detail-error-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.detail-error-table th {
  background: #ececec;
  color: #4d4f5c;
  text-align: left;
  padding: 8px 10px;
  font-weight: 700;
  font-size: 14px;
  position: sticky;
  top: 0;
  z-index: 1;
}
.detail-error-table td {
  padding: 8px 10px;
  border-bottom: 1px solid #f3f3f5;
  color: #333;
}

.error-cell {
  color: rgb(231, 96, 103) !important;
  font-weight: 600;
  text-transform: uppercase;
}
.error-table-row {
  cursor: pointer;
  transition: background 0.1s;
}
.error-table-row:hover {
  background: #fff5f5;
}
.error-row--selected {
  background: #fff0f0 !important;
  border-left: 3px solid rgb(231, 96, 103);
}

::v-deep .v-dialog:not(.v-dialog--fullscreen) {
  max-height: 100%;
}

.capture-table-card {
  overflow: hidden;
  background: #fff;
}

.capture-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #e0e0e0;
  font-size: 13px;
}
.capture-table th {
  background: #ececec;
  color: #4d4f5c;
  text-align: left;
  padding: 8px 10px;
  font-weight: 700;
  font-size: 14px;
}
.capture-table td {
  padding: 8px 10px;
  border-top: 1px solid #eee;
}
.index-col {
  width: 60px;
}
.capture-table-scroll {
  max-height: 280px;
  overflow-y: auto;
}
.capture-modal-footer {
  display: flex;
  justify-content: center;
  padding-top: 20px;
}
.no-image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #777;
  font-size: 12px;
  font-weight: 500;
}
::v-deep ::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
::v-deep ::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 4px;
}
::v-deep ::-webkit-scrollbar-thumb {
  background: #c5cdd6;
  border-radius: 4px;
}
::v-deep ::-webkit-scrollbar-thumb:hover {
  background: #9aa0ac;
}

.zoom-dialog-card {
  height: 80vh;
}

.image-viewport {
  flex: 1;
  overflow: hidden;
  position: relative;
  user-select: none;
  -webkit-user-select: none;
    background-color: #ececec;
}

.zoomable-image {
  position: absolute;
  top: 0;
  left: 0;
  display: block;
  max-width: none;
  pointer-events: none;
}

.no-image-placeholder {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #aaa;
  font-size: 14px;
}

.zoom-label {
  color: #fff;
  font-size: 13px;
  min-width: 48px;
  text-align: center;
  display: inline-block;
}
</style>
